import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { DashboardService, StorageUsage } from './dashboard.service';

/**
 * Estado do snapshot de uso de armazenamento (GET /v1/dashboard/storage-usage).
 *
 * Garante a regra "busca ÚNICA por sessão de aplicação": o endpoint é chamado
 * apenas (a) logo após o login e (b) no bootstrap com sessão já autenticada
 * (F5/refresh). Qualquer outro consumidor (card da sidebar, alerta de limite)
 * LÊ o snapshot em cache — nenhuma navegação/request HTTP refaz a chamada
 * durante a sessão.
 *
 * Comportamento silencioso em erro/403: a falha é cacheada como `null` e o
 * card segue com fallback ("Armazenamento não disponível"), sem quebrar o app.
 */
@Injectable({
    providedIn: 'root'
})
export class StorageUsageService
{
    private _usage$: BehaviorSubject<StorageUsage | null> = new BehaviorSubject<StorageUsage | null>(null);
    private _resolved$: Subject<void> = new Subject<void>();
    private _loaded: boolean = false;
    private _fetching: boolean = false;

    /**
     * Constructor
     */
    constructor(private _dashboardService: DashboardService)
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Snapshot atual (null enquanto não carregado ou após erro).
     */
    get usage(): StorageUsage | null
    {
        return this._usage$.value;
    }

    /**
     * True quando a busca única da sessão já terminou (sucesso OU erro/403).
     */
    get loaded(): boolean
    {
        return this._loaded;
    }

    /**
     * Observable do snapshot — emite o último valor conhecido do cache.
     */
    get storageUsage$(): Observable<StorageUsage | null>
    {
        return this._usage$.asObservable();
    }

    /**
     * Sinal de término da busca única (valor, null ou erro) — usado pelo
     * alerta de limite para aguardar o snapshot antes de avaliar critérios.
     */
    get resolved$(): Observable<void>
    {
        return this._resolved$.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Dispara a busca ÚNICA do snapshot por sessão de aplicação.
     *
     * Idempotente: chamadas repetidas (login, layout, alerta) NÃO geram novas
     * requests — apenas a 1ª por sessão busca o endpoint; as demais leem o
     * cache. Erro/403 é cacheado como null (silencioso, fallback preservado).
     */
    load(): void
    {
        // Já terminou (sucesso ou erro) ou está em voo → não re-busca.
        if ( this._loaded || this._fetching )
        {
            return;
        }

        this._fetching = true;

        this._dashboardService.getStorageUsage()
            .pipe(
                take(1),
                map((response) => response?.data ?? null),
                catchError(() => of(null))
            )
            .subscribe((usage: StorageUsage | null) => {
                this._usage$.next(usage);
                this._loaded = true;
                this._fetching = false;
                this._resolved$.next();
            });
    }

    /**
     * Nova sessão (login/logout): limpa o cache e refaz a busca única no
     * próximo load() — garante que cada login busque dados frescos do tenant.
     */
    reset(): void
    {
        this._loaded = false;
        this._fetching = false;
        this._usage$.next(null);
    }

    /**
     * Rebusca explícita iniciada pelo USUÁRIO (ex.: clique no card ainda sem
     * dados após erro inicial). Não é disparada por navegação — apenas por
     * ação explícita, preservando a regra de 1 chamada por sessão nas demais.
     */
    refresh(): void
    {
        this.reset();
        this.load();
    }
}